import React, { forwardRef, useRef, useImperativeHandle, useEffect, useState, Suspense } from 'react'
import { createPortal } from 'react-dom'
// Eager load ThreeEffectsOverlay to preload R3F Canvas and all dependencies at app start
// This eliminates the 6-second delay on first activation
import ThreeEffectsOverlay from './effects3d/ThreeEffectsOverlay'
// Also import HammerSlam2D to trigger texture preloading
import './effects3d/HammerSlam2D'
import { createPsyBlockEffect, renderPsyBlockEffect, type GooNode, type GooConnection, type EdgeConnection } from './effects/psyBlockEffect'
import { createHealEffect, renderHealEffect } from './effects/healEffect'
import { createAttackEffect, renderAttackEffect } from './effects/attackEffect'
import { createShockEffect, renderShockEffect } from './effects/shockEffect'
import { createEnergizeEffect, renderEnergizeEffect } from './effects/energizeEffect'
import { createStealEffect, renderStealEffect } from './effects/stealEffect'
import { createDoubleCrossEffect, renderDoubleCrossEffect } from './effects/doubleCrossEffect'
import { createDispelEffect, renderDispelEffect } from './effects/dispelEffect'
import { createHibernationEffect, renderHibernationEffect } from './effects/hibernationEffect'
import { createFireballEffect, renderFireballEffect } from './effects/fireballEffect'
import { createTremblingEffect, renderTremblingEffect } from './effects/tremblingEffect'
import { createLifeDrainEffect, renderLifeDrainEffect } from './effects/lifeDrainEffect'
import { createSpriteEffect, renderSpriteEffect, type SpriteEffectData } from './effects/spriteEffect'
import { createHammerSlamEffect, renderHammerSlamEffect } from './effects/hammerSlamEffect'
import { createVoodooDollEffect, renderVoodooDollEffect } from './effects/voodooDollEffect'
import { createDiscerningEyeEffect, renderDiscerningEyeEffect } from './effects/discerningEyeEffect'
import { createProtectorEffect, renderProtectorEffect } from './effects/protectorEffect'
import { createEnergySwordEffect, renderEnergySwordEffect } from './effects/energySwordEffect'
import { createLarcendaggerEffect, renderLarcendaggerEffect } from './effects/larcendaggerEffect'
import { createInfernoGauntletEffect, renderInfernoGauntletEffect } from './effects/infernoGauntletEffect'
import { createFlyingScytheEffect, renderFlyingScytheEffect } from './effects/flyingScytheEffect'
import { createPlantedBombEffect, renderPlantedBombEffect } from './effects/plantedBombEffect'
import { createInvincibleEffect, renderInvincibleEffect } from './effects/invincibleEffect'
import { createLifeChargerEffect, renderLifeChargerEffect } from './effects/lifeChargerEffect'
import { BrokenSecretEffect } from './effects/brokenSecretEffect'
import { createSealOfSilenceEffect, renderSealOfSilenceEffect } from './effects/sealOfSilenceEffect'
import { createEyesOfMadnessEffect, renderEyesOfMadnessEffect } from './effects/eyesOfMadnessEffect'
import { createMadnessControlEffect, renderMadnessControlEffect } from './effects/madnessControlEffect'
import { createTwilightShiftEffect, renderTwilightShiftEffect } from './effects/twilightShiftEffect'
import { createChromabowEffect, renderChromabowEffect } from './effects/chromabowEffect'
import { createAstridBowEffect, renderAstridBowEffect } from './effects/astridBowEffect'
import { createShadowBurglaryEffect, renderShadowBurglaryEffect } from './effects/shadowBurglaryEffect'
import { createNaturalGripEffect, renderNaturalGripEffect } from './effects/naturalGripEffect'
import { createThunderSpiritEffect, renderThunderSpiritEffect } from './effects/thunderSpiritEffect'
import { createCardCycloneEffect, renderCardCycloneEffect } from './effects/cardCycloneEffect'
import { createCounterblastEffect, renderCounterblastEffect } from './effects/counterblastEffect'
import { createSwordOfRevengeEffect, renderSwordOfRevengeEffect } from './effects/swordOfRevengeEffect'
import { createAncientSwordEffect, renderAncientSwordEffect } from './effects/ancientSwordEffect'
import { createAncientShieldEffect, renderAncientShieldEffect } from './effects/ancientShieldEffect'
import { createSteelShieldEffect, renderSteelShieldEffect } from './effects/steelShieldEffect'
import { createShimmeringShieldEffect, renderShimmeringShieldEffect } from './effects/shimmeringShieldEffect'
import { createTheStingEffect, renderTheStingEffect } from './effects/theStingEffect'
import { createCursedIronEffect, renderCursedIronEffect } from './effects/cursedIronEffect'
import { createAleEffect, renderAleEffect } from './effects/aleEffect'
import { createBodyUpgradeEffect, renderBodyUpgradeEffect } from './effects/bodyUpgradeEffect';
import { createPurificationState, updatePurification, drawPurification, type PurificationState } from './effects/purificationEffect'
import { createFortunesFavorEffect, renderFortunesFavorEffect } from './effects/fortunesFavorEffect'
import { createArcaneBootsEffect, renderArcaneBootsEffect } from './effects/arcaneBootsEffect'
import { createSpikesEffect, renderSpikesEffect } from './effects/spikesEffect'
import { getAsset } from '../services/assets'
export type Particle = { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number }

type Effect = {
  kind: string
  t: number
  x?: number
  y?: number
  w?: number
  h?: number
  x2?: number
  y2?: number
  data?: any
} | SpriteEffectData

export type ParticleLayerHandle = {
  spawnHit: (x: number, y: number) => void
  spawnHeal: (x: number, y: number, w?: number, h?: number) => void
  // ... other spawns ...
  spawnSprite: (x: number, y: number, config: {
    spriteUrl: string,
    frameWidth: number,
    frameHeight: number,
    totalFrames: number,
    fps: number,
    loop?: boolean,
    scale?: number
  }) => void
  spawnAttackEpic: (x: number, y: number) => void
  spawnPushbackShield: () => void
  spawnDispelShush: () => void
  spawnHibernationIce: (x: number, y: number, w: number, h: number) => void
  spawnDiseaseSlime: (x: number, y: number, w: number, h: number) => void
  spawnStealGhostHand: (sx: number, sy: number, tx: number, ty: number) => void
  spawnDoubleCrossCircle: (x: number, y: number) => void
  spawnEnergizeEchoes: (targetId?: string) => void
  spawnShockLightning: (sourceX: number, sourceY: number, targetX: number, targetY: number, targetId: string) => void
  spawnPsySearchWeb: () => void
  spawnPsyBlockHands: (x: number, y: number) => void
  spawnFireball: (sx: number, sy: number, tx: number, ty: number) => void
  spawnTrembling: (sx?: number, sy?: number, tx?: number, ty?: number) => void
  spawnLifeDrain: (sx: number, sy: number, tx: number, ty: number) => void
  spawnHammerSlam: (sx: number, sy: number, tx: number, ty: number) => void
  spawn3DHammerSlam: (sx: number, sy: number, tx: number, ty: number, isTargetPerspective?: boolean) => void
  spawnVoodooDoll: (isOpponent?: boolean) => void
  spawnDiscerningEye: (isOpponent?: boolean) => void
  spawnProtector: (isAttack: boolean, isOpponent?: boolean) => void
  spawnEnergySword: (x: number, y: number, isOpponent?: boolean) => void
  spawnLarcendagger: (ox: number, oy: number, tx: number, ty: number, isOpponent?: boolean) => void
  spawnInfernoGauntlet: (ox: number, oy: number, tx: number, ty: number, isOpponent?: boolean) => void
  spawnFlyingScythe: (ox: number, oy: number, tx: number, ty: number, isOpponent?: boolean) => void
  spawnPlantedBomb: (sx: number, sy: number, tx: number, ty: number, isOpponent?: boolean) => void
  spawnInvincible: (tx: number, ty: number) => void
  spawnLifeCharger: (tx: number, ty: number) => void
  spawnBrokenSecret: (sx: number, sy: number, tx: number, ty: number) => void
  spawnSealOfSilence: (sx: number, sy: number, tx: number, ty: number) => void
  spawnEyesOfMadness: (cx: number, cy: number) => void
  spawnMadnessControl: (cx: number, cy: number) => void
  spawnTwilightShiftEffect: (sx: number, sy: number) => void
  spawnChromabow: (sx: number, sy: number, tx: number, ty: number, isOpponent?: boolean) => void
  spawnAstridBow: (sx: number, sy: number, tx: number, ty: number) => void
  spawnShadowBurglary: (sx: number, sy: number, tx: number, ty: number) => void
  spawnNaturalGrip: (tx: number, ty: number, isOpponent?: boolean) => void
  spawnThunderSpirit: (tx: number, ty: number) => void
  spawnCardCyclone: (tx: number, ty: number, opp: boolean) => void
  spawnCounterblast: (sx: number, sy: number, tx: number, ty: number) => void
  spawnSwordOfRevenge: (sx: number, sy: number, tx: number, ty: number) => void
  spawnAncientSword: (sx: number, sy: number, tx: number, ty: number) => void
  spawnAncientShield: (tx: number, ty: number, isOpponent?: boolean) => void
  spawnSteelShield: (tx: number, ty: number, isOpponent?: boolean) => void
  spawnShimmeringShield: (tx: number, ty: number, isOpponent?: boolean) => void
  spawnTheSting: (sx: number, sy: number, tx: number, ty: number, isOpponent?: boolean) => void
  spawnCursedIron: (tx: number, ty: number, isOpponent?: boolean) => void
  spawnAle: (tx: number, ty: number) => void
  spawnBodyUpgrade: (tx: number, ty: number, isOpponent?: boolean) => void;
  spawnPurification: (sx: number, sy: number, tx: number, ty: number) => void
  spawnFortunesFavor: (tx: number, ty: number) => void
  spawnArcaneBoots: (tx: number, ty: number) => void
  spawnSpikes: (dx: number, dy: number, atx: number, aty: number) => void
}

// Type for 3D effects
type Effect3D = { id: string; type: 'hammerSlam'; sx: number; sy: number; tx: number; ty: number; isTargetPerspective?: boolean }

const ParticleLayer = forwardRef<ParticleLayerHandle, { enabled: boolean }>((props, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const particlesRef = useRef<Particle[]>([])
  const rafRef = useRef<number | null>(null)
  const effectsRef = useRef<Effect[]>([])
  const startCanvasLoopRef = useRef<(() => void) | null>(null)
  const lastTsRef = useRef(0)
  const [effects3D, setEffects3D] = useState<Effect3D[]>([])

  // Custom states for highly specialized effects
  const purificationEffectsRef = useRef<PurificationState[]>([])
  const sacredCrystalImgRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    const img = new Image();
    img.src = '/assets/effects/purification/sacred_crystal.svg';
    img.onload = () => { sacredCrystalImgRef.current = img; };
  }, []);

  // Sprite Cache Map (Color + Size -> HTMLCanvasElement)
  // Moved to ref so it persists across renders without triggering them
  const spriteCacheRef = useRef<Map<string, HTMLCanvasElement>>(new Map())

  // Helper to get or create a pre-rendered particle sprite
  const getParticleSprite = (color: string, size: number): HTMLCanvasElement => {
    // Quantize size to avoid too many cache entries (e.g., round to nearest whole number)
    const qSize = Math.max(1, Math.round(size))
    const key = `${color}-${qSize}`
    const cache = spriteCacheRef.current

    let sprite = cache.get(key)
    if (!sprite) {
      sprite = document.createElement('canvas')
      const padding = 2 // extra space for antialiasing just in case
      const diameter = qSize * 2
      sprite.width = diameter + padding * 2
      sprite.height = diameter + padding * 2
      const sCtx = sprite.getContext('2d')
      if (sCtx) {
        sCtx.fillStyle = color
        sCtx.beginPath()
        sCtx.arc(sprite.width / 2, sprite.height / 2, qSize, 0, Math.PI * 2)
        sCtx.fill()
      }
      cache.set(key, sprite)
    }
    return sprite
  }

  const spawn = (kind: 'hit' | 'heal', x: number, y: number) => {
    // ... spawn logic ...
    if (!props.enabled) return
    const arr = particlesRef.current
    const count = kind === 'hit' ? 20 : 25
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2
      const speed = kind === 'hit' ? (2 + Math.random() * 3) : (1 + Math.random() * 2)
      const color = kind === 'hit' ? `rgba(239,68,68,${0.7 + Math.random() * 0.3})` : (Math.random() < 0.5 ? `rgba(34,197,94,${0.6 + Math.random() * 0.3})` : `rgba(234,179,8,${0.6 + Math.random() * 0.3})`)
      arr.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - (kind === 'heal' ? 1.5 : 0), life: 800 + Math.random() * 600, color, size: kind === 'hit' ? 3 : 2 })
    }
    startCanvasLoopRef.current?.()
  }

  const spawnEffect = (e: Effect) => {
    if (!props.enabled) return
    effectsRef.current.push(e)
    startCanvasLoopRef.current?.()
  }

  useImperativeHandle(ref, () => ({
    spawnHit: (x, y) => spawn('hit', x, y),
    spawnSprite: (x, y, config) => {
      spawnEffect(createSpriteEffect(x, y, config))
    },
    spawnAttackEpic: (x, y, w = 0, h = 0) => {
      // ... rest of spawns ...
      spawnEffect(createAttackEffect(x, y, w, h))
    },
    spawnPushbackShield: () => {
      const cx = window.innerWidth / 2
      const cy = window.innerHeight / 2
      const waves = []
      // Create initial waves - More waves for "waving" effect
      for (let i = 0; i < 12; i++) {
        waves.push({
          y: cy + 120 + i * 15, // Start lower and closer together
          width: 80 + i * 30,
          height: 30 + i * 8,
          speed: 12 + i * 1.5,
          opacity: 1.0,
          thickness: 8 + i * 1.5
        })
      }
      const particles = []
      for (let i = 0; i < 50; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.2 // Tighter upward cone
        const speed = 15 + Math.random() * 25
        particles.push({
          x: cx,
          y: cy + 150,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1.0,
          size: 2 + Math.random() * 4,
          color: Math.random() > 0.5 ? '#ef4444' : '#b91c1c'
        })
      }

      spawnEffect({
        kind: 'pushback',
        t: 0,
        data: {
          pushback: {
            waves,
            particles
          }
        }
      })
    },
    spawnDispelShush: () => {
      const cx = window.innerWidth / 2
      const cy = window.innerHeight / 2
      spawnEffect(createDispelEffect(cx, cy))
    },
    spawnHeal: (x, y, w = 0, h = 0) => {
      spawnEffect(createHealEffect(x, y, w, h))
    },
    spawnHibernationIce: (x, y, w, h) => spawnEffect(createHibernationEffect(x, y, w, h)),
    spawnDiseaseSlime: (x, y, w: number, h: number) => spawnEffect({ kind: 'disease', t: 0, x, y, w, h }),
    spawnStealGhostHand: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect(createStealEffect(sx, sy, tx, ty))
    },
    spawnDoubleCrossCircle: (x, y) => spawnEffect(createDoubleCrossEffect(x, y)),
    spawnEnergizeEchoes: (targetId = 'Astrid') => {
      const cx = window.innerWidth / 2
      const cy = window.innerHeight / 2
      spawnEffect(createEnergizeEffect(cx, cy, targetId))
    },
    spawnShockLightning: (sx, sy, tx, ty, tid) => {
      spawnEffect(createShockEffect(sx, sy, tx, ty, tid))
    },
    spawnPsySearchWeb: () => {
      const cx = window.innerWidth / 2
      const cy = window.innerHeight / 2
      const nodes: GooNode[] = []
      const nodeCount = 12
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: cx + (Math.random() - 0.5) * 200,
          y: cy + (Math.random() - 0.5) * 200,
          vx: (Math.random() - 0.5) * 2,
          vy: (Math.random() - 0.5) * 2,
          size: 10 + Math.random() * 15,
          baseSize: 10 + Math.random() * 15,
          phase: Math.random() * Math.PI * 2
        })
      }
      const connections: GooConnection[] = []
      // Connect nodes to form a rough network
      for (let i = 0; i < nodeCount; i++) {
        const neighbors = Math.floor(2 + Math.random() * 2)
        for (let j = 0; j < neighbors; j++) {
          const target = (i + 1 + Math.floor(Math.random() * (nodeCount - 2))) % nodeCount
          if (!connections.some(c => (c.from === i && c.to === target) || (c.from === target && c.to === i))) {
            connections.push({ from: i, to: target, length: 100 + Math.random() * 100, pulses: [] })
          }
        }
      }
      const edgeConnections: EdgeConnection[] = []
      // Connect some nodes to screen edges
      for (let i = 0; i < 8; i++) {
        const nodeIndex = Math.floor(Math.random() * nodeCount)
        const side = Math.floor(Math.random() * 4) // 0: top, 1: right, 2: bottom, 3: left
        let ex = 0, ey = 0
        if (side === 0) { ex = Math.random() * window.innerWidth; ey = 0 }
        else if (side === 1) { ex = window.innerWidth; ey = Math.random() * window.innerHeight }
        else if (side === 2) { ex = Math.random() * window.innerWidth; ey = window.innerHeight }
        else { ex = 0; ey = Math.random() * window.innerHeight }
        edgeConnections.push({ nodeIndex, x: ex, y: ey, pulses: [] })
      }

      spawnEffect({
        kind: 'psySearch',
        t: 0,
        data: { nodes, connections, edgeConnections }
      })
    },
    spawnPsyBlockHands: (x, y) => {
      spawnEffect(createPsyBlockEffect(x, y))
    },
    spawnFireball: (sx, sy, tx, ty) => {
      spawnEffect(createFireballEffect(sx, sy, tx, ty))
    },
    spawnTrembling: (sx, sy, tx, ty) => {
      spawnEffect(createTremblingEffect(sy, ty))
    },
    spawnLifeDrain: (sx, sy, tx, ty) => {
      spawnEffect(createLifeDrainEffect(sx, sy, tx, ty))
    },
    spawnHammerSlam: (sx, sy, tx, ty) => {
      spawnEffect(createHammerSlamEffect(sx, sy, tx, ty))
    },
    spawn3DHammerSlam: (sx, sy, tx, ty, isTargetPerspective = false) => {
      setEffects3D(prev => [...prev, {
        id: `slam-${Date.now()}`,
        type: 'hammerSlam',
        sx, sy, tx, ty,
        isTargetPerspective
      }])
    },
    spawnVoodooDoll: (isOpponent = false) => {
      spawnEffect(createVoodooDollEffect(isOpponent))
    },
    spawnDiscerningEye: (isOpponent = false) => {
      spawnEffect(createDiscerningEyeEffect(isOpponent))
    },
    spawnProtector: (isAttack: boolean, isOpponent = false) => {
      spawnEffect(createProtectorEffect(isAttack, isOpponent))
    },
    spawnEnergySword: (x: number, y: number, isOpponent = false) => {
      spawnEffect(createEnergySwordEffect(x, y, isOpponent))
    },
    spawnLarcendagger: (ox: number, oy: number, tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createLarcendaggerEffect(ox, oy, tx, ty, isOpponent))
    },
    spawnInfernoGauntlet: (ox: number, oy: number, tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createInfernoGauntletEffect(ox, oy, tx, ty, isOpponent))
    },
    spawnFlyingScythe: (ox: number, oy: number, tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createFlyingScytheEffect(ox, oy, tx, ty, isOpponent))
    },
    spawnPlantedBomb: (sx: number, sy: number, tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createPlantedBombEffect(sx, sy, tx, ty, isOpponent))
    },
    spawnInvincible: (tx: number, ty: number) => {
      spawnEffect(createInvincibleEffect(tx, ty))
    },
    spawnLifeCharger: (tx: number, ty: number) => {
      spawnEffect(createLifeChargerEffect(tx, ty))
    },
    spawnBrokenSecret: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect({
        kind: 'brokenSecret',
        t: 0,
        data: new BrokenSecretEffect(sx, sy, tx, ty)
      })
    },
    spawnSealOfSilence: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect(createSealOfSilenceEffect(sx, sy, tx, ty))
    },
    spawnEyesOfMadness: (cx: number, cy: number) => {
      spawnEffect(createEyesOfMadnessEffect(cx, cy))
    },
    spawnMadnessControl: (cx: number, cy: number) => {
      spawnEffect(createMadnessControlEffect(cx, cy))
    },
    spawnTwilightShiftEffect: (sx: number, sy: number) => {
      spawnEffect(createTwilightShiftEffect(sx, sy))
    },
    spawnChromabow: (sx: number, sy: number, tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createChromabowEffect(sx, sy, tx, ty)) // isOpponent isn't inherently handled by the base effect signature, but we could pass it if we update the effect. Wait, createChromabowEffect takes sx, sy, tx, ty. Let's just pass sx, sy, tx, ty.
    },
    spawnAstridBow: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect(createAstridBowEffect(sx, sy, tx, ty))
    },
    spawnShadowBurglary: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect(createShadowBurglaryEffect(sx, sy, tx, ty))
    },
    spawnNaturalGrip: (tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createNaturalGripEffect(tx, ty))
    },
    spawnThunderSpirit: (tx: number, ty: number) => {
      spawnEffect(createThunderSpiritEffect(tx, ty))
    },
    spawnCardCyclone: (tx: number, ty: number, opp: boolean) => {
      spawnEffect(createCardCycloneEffect(tx, ty, opp))
    },
    spawnCounterblast: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect(createCounterblastEffect(sx, sy, tx, ty))
    },
    spawnSwordOfRevenge: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect(createSwordOfRevengeEffect(sx, sy, tx, ty))
    },
    spawnAncientSword: (sx: number, sy: number, tx: number, ty: number) => {
      spawnEffect(createAncientSwordEffect(sx, sy, tx, ty))
    },
    spawnAncientShield: (tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createAncientShieldEffect(tx, ty, isOpponent))
    },
    spawnSteelShield: (tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createSteelShieldEffect(tx, ty, isOpponent))
    },
    spawnShimmeringShield: (tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createShimmeringShieldEffect(tx, ty, isOpponent))
    },
    spawnTheSting: (sx: number, sy: number, tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createTheStingEffect(sx, sy, tx, ty, isOpponent))
    },
    spawnCursedIron: (tx: number, ty: number, isOpponent = false) => {
      spawnEffect(createCursedIronEffect(tx, ty, isOpponent))
    },
    spawnAle: (tx: number, ty: number) => {
      spawnEffect(createAleEffect(tx, ty))
    },
    spawnBodyUpgrade: (tx: number, ty: number, isOpponent?: boolean) => {
      spawnEffect(createBodyUpgradeEffect(tx, ty, isOpponent));
    },
    spawnPurification: (sx, sy, tx, ty) => {
      if (!props.enabled) return;
      purificationEffectsRef.current.push(
        createPurificationState(`purification-${Date.now()}`, sx, sy, tx, ty)
      );
      startCanvasLoopRef.current?.()
    },
    spawnFortunesFavor: (tx: number, ty: number) => {
      spawnEffect(createFortunesFavorEffect(tx, ty));
    },
    spawnArcaneBoots: (tx: number, ty: number) => {
      spawnEffect(createArcaneBootsEffect(tx, ty));
    },
    spawnSpikes: (dx: number, dy: number, atx: number, aty: number) => {
      spawnEffect(createSpikesEffect(dx, dy, atx, aty));
    }
  }))

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const hasActiveCanvasWork = () => (
      particlesRef.current.length > 0 ||
      effectsRef.current.length > 0 ||
      purificationEffectsRef.current.length > 0
    )
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight }
    const clearCanvas = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    resize()
    const step = (ts: number) => {
      rafRef.current = null
      if (!props.enabled) {
        clearCanvas()
        lastTsRef.current = 0
        return
      }

      const dtMs = lastTsRef.current === 0 ? 0 : Math.min(ts - lastTsRef.current, 50)
      lastTsRef.current = ts
      const dtFactor = dtMs / 16.67

      const arr = particlesRef.current
      const effs = effectsRef.current
      clearCanvas()
      for (let i = arr.length - 1; i >= 0; i--) {
        const p = arr[i]
        const age = (p.life -= dtMs)
        if (age <= 0) {
          // O(1) swap-and-pop removal for performance
          arr[i] = arr[arr.length - 1]
          arr.pop()
          continue
        }
        p.x += p.vx * dtFactor
        p.y += p.vy * dtFactor
        if (Math.random() < 0.05 * dtFactor) p.vx *= Math.pow(0.98, dtFactor)

        // Sprite-based rendering instead of expensive arc+fill
        const alpha = Math.max(0, age / 1200)
        ctx.globalAlpha = alpha

        let sprite = getParticleSprite(p.color, p.size)
        ctx.drawImage(sprite, p.x - p.size, p.y - p.size)
      }

      // ✅ ADDED: Reset context state to prevent leaks
      ctx.globalAlpha = 1.0

      // Update and Draw Purification Effects
      const purifications = purificationEffectsRef.current;
      for (let i = purifications.length - 1; i >= 0; i--) {
        const pState = purifications[i];
        const isActive = updatePurification(pState, dtMs, () => {
          // Handle completion inside update if needed, but we check boolean return
        });

        if (!isActive) {
          purifications.splice(i, 1);
        } else {
          drawPurification(ctx, pState, sacredCrystalImgRef.current);
        }
      }

      for (let i = effs.length - 1; i >= 0; i--) {
        const e = effs[i]
        e.t += dtMs
        ;(e as any)._df = dtFactor
        if (e.kind === 'attackEpic') {
          if (!renderAttackEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'naturalGrip') {
          if (!renderNaturalGripEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'pushback') {
          const maxTime = 1200
          const tt = e.t / maxTime
          if (tt > 1.0) { effs.splice(i, 1); continue }

          const data = e.data!.pushback!
          const cx = canvas.width / 2
          const cy = canvas.height / 2

          // Update Physics
          // Waves move UP (negative Y) and expand
          data.waves.forEach((w: any) => {
            w.y -= w.speed * dtFactor
            w.width += w.speed * 0.6 * dtFactor
            w.height += w.speed * 0.2 * dtFactor
            w.opacity -= 0.015 * dtFactor
            w.thickness += 0.3 * dtFactor
          })
          data.waves = data.waves.filter((w: any) => w.opacity > 0)

          // Particles
          data.particles.forEach((p: any) => {
            p.x += p.vx * dtFactor
            p.y += p.vy * dtFactor
            p.life -= 0.025 * dtFactor
            p.size *= Math.pow(0.96, dtFactor)
          })
          data.particles = data.particles.filter((p: any) => p.life > 0)

          ctx.save()

          // 1. Draw Waves (The Force Push)
          // "gradiently fading to the edges ... further from the center more transparrent"
          ctx.globalCompositeOperation = 'screen' // Glow effect

          data.waves.forEach((w: any) => {
            ctx.save()
            ctx.globalAlpha = w.opacity

            // Horizontal Gradient for transparency
            // Center = Opaque, Edges = Transparent
            const grad = ctx.createLinearGradient(cx - w.width, 0, cx + w.width, 0)
            grad.addColorStop(0, 'rgba(239, 68, 68, 0)')
            grad.addColorStop(0.2, 'rgba(239, 68, 68, 0.1)')
            grad.addColorStop(0.5, 'rgba(239, 68, 68, 1)')
            grad.addColorStop(0.8, 'rgba(239, 68, 68, 0.1)')
            grad.addColorStop(1, 'rgba(239, 68, 68, 0)')

            // Core Wave
            ctx.beginPath()
            ctx.ellipse(cx, w.y, w.width, w.height, 0, Math.PI, 0) // Top half arc
            ctx.strokeStyle = grad
            ctx.lineWidth = w.thickness
            ctx.lineCap = 'round'
            ctx.stroke()

            // Inner Highlight (Thinner, brighter)
            const highlightGrad = ctx.createLinearGradient(cx - w.width, 0, cx + w.width, 0)
            highlightGrad.addColorStop(0, 'rgba(252, 165, 165, 0)')
            highlightGrad.addColorStop(0.5, 'rgba(252, 165, 165, 0.8)')
            highlightGrad.addColorStop(1, 'rgba(252, 165, 165, 0)')

            ctx.beginPath()
            ctx.ellipse(cx, w.y + 4, w.width * 0.95, w.height * 0.95, 0, Math.PI, 0)
            ctx.strokeStyle = highlightGrad
            ctx.lineWidth = w.thickness * 0.3
            ctx.stroke()

            // Outer Glow (Blurry)
            ctx.shadowColor = '#dc2626'
            ctx.shadowBlur = 20
            ctx.beginPath()
            ctx.ellipse(cx, w.y, w.width, w.height, 0, Math.PI, 0)
            ctx.strokeStyle = grad // Reuse base gradient
            ctx.lineWidth = w.thickness * 0.5
            ctx.stroke()
            ctx.shadowBlur = 0

            ctx.restore()
          })

          // 2. Draw Particles (Debris/Sparks)
          data.particles.forEach((p: any) => {
            ctx.globalAlpha = p.life
            ctx.fillStyle = p.color

            // Stretch based on velocity for speed effect
            const speed = Math.hypot(p.vx, p.vy)
            const angle = Math.atan2(p.vy, p.vx)

            ctx.save()
            ctx.translate(p.x, p.y)
            ctx.rotate(angle)
            ctx.scale(1 + speed * 0.15, 1) // More stretch

            ctx.beginPath()
            ctx.arc(0, 0, p.size, 0, Math.PI * 2)
            ctx.fill()

            ctx.restore()
          })

          ctx.restore()
        } else if (e.kind === 'dispel') {
          if (!renderDispelEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'hibernation') {
          if (!renderHibernationEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'disease') {
          const maxTime = 7200 // 3x longer duration for cloud
          const tt = e.t / maxTime
          if (tt > 1.0) { effs.splice(i, 1); continue }

          const tx = e.x! + e.w! / 2
          const ty = e.y! + e.h! / 2
          const floorY = e.y! + e.h! // Bottom of avatar for floor collision
          const ceilingY = e.y! // Top of avatar for snot origin

          if (!e.data) {
            e.data = {
              liquidParticles: [],
              gasParticles: [],
              snotParticles: []
            }
            // Init Liquid Particles (Splash) - Keep original count/speed
            for (let k = 0; k < 40; k++) {
              e.data.liquidParticles.push({
                x: tx,
                y: ty - 20,
                vx: (Math.random() - 0.5) * 10,
                vy: (Math.random() - 0.5) * 10 - 5,
                size: 3 + Math.random() * 5,
                color: Math.random() > 0.5 ? '#84cc16' : '#65a30d',
                life: 1.0
              })
            }
            // Init Gas Particles (Cloud)
            // Increased density (+100% more) and size (+100% more)
            for (let k = 0; k < 60; k++) {
              e.data.gasParticles.push({
                x: tx + (Math.random() - 0.5) * e.w! * 1.5,
                y: ty + (Math.random() - 0.5) * e.h! * 1.2,
                vx: (Math.random() - 0.5) * 0.5,
                vy: -0.2 - Math.random() * 0.3,
                size: 90 + Math.random() * 120, // Doubled size
                phase: Math.random() * Math.PI * 2,
                life: 1.0
              })
            }
            // Init Snot Particles (Dripping Goo)
            for (let k = 0; k < 12; k++) {
              e.data.snotParticles.push({
                x: tx + (Math.random() - 0.5) * e.w! * 0.7,
                y: ceilingY - 10, // Start above
                vx: 0,
                vy: 0.5 + Math.random() * 1.5, // Slow drip
                size: 4 + Math.random() * 6,
                anchorY: ceilingY - 10,
                maxLen: e.h! * (0.5 + Math.random() * 0.8), // Drip length
                phase: Math.random() * Math.PI * 2
              })
            }
          }

          const data = e.data

          ctx.save()

          // 1. Draw Gas Cloud (Background)
          // Fade in/out based on global time (slow)
          const gasOpacity = tt < 0.1 ? tt * 10 : (tt > 0.9 ? (1 - tt) * 10 : 1)

          data.gasParticles.forEach((p: any) => {
            p.x += p.vx * dtFactor
            p.y += p.vy * dtFactor
            p.size += 0.05 * dtFactor

            const alpha = Math.sin(e.t * 0.001 + p.phase) * 0.2 + 0.3
            ctx.globalAlpha = alpha * gasOpacity * 0.6

            const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size)
            grad.addColorStop(0, 'rgba(163, 230, 53, 0.4)')
            grad.addColorStop(1, 'rgba(163, 230, 53, 0)')
            ctx.fillStyle = grad
            ctx.beginPath()
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
            ctx.fill()
          })

          // 2. Draw Snot Layer (Middle)
          // "Connected sticky liquid... 20% transparency"
          ctx.globalAlpha = 0.2 * (tt < 0.1 ? tt * 10 : (tt > 0.9 ? (1 - tt) * 10 : 1))
          ctx.fillStyle = '#84cc16'
          ctx.strokeStyle = '#84cc16'
          ctx.lineCap = 'round'

          data.snotParticles.forEach((p: any) => {
            // Drip physics
            if (p.y < p.anchorY + p.maxLen) {
              p.y += p.vy * dtFactor
            } else {
              // Drip finished, maybe oscillate or detach? 
              // Let's just hang there bobbing slightly
              p.y = p.anchorY + p.maxLen + Math.sin(e.t * 0.005 + p.phase) * 5
            }

            // Draw connections (snot strands)
            ctx.lineWidth = p.size
            ctx.beginPath()
            ctx.moveTo(p.x, p.anchorY)
            // Bezier for gooey stretch
            const midY = (p.anchorY + p.y) / 2
            const wobble = Math.sin(e.t * 0.01 + p.phase) * 5
            ctx.quadraticCurveTo(p.x + wobble, midY, p.x, p.y)
            ctx.stroke()

            // Draw drop
            ctx.beginPath()
            ctx.arc(p.x, p.y, p.size * 1.2, 0, Math.PI * 2)
            ctx.fill()
          })

          // 3. Draw Liquid Splashes (Foreground)
          ctx.globalAlpha = 1
          const gravity = 0.4

          data.liquidParticles.forEach((p: any) => {
            // Only simulate if still relevant (optimization)
            if (p.y < floorY + 100) {
              p.vy += gravity * dtFactor
              p.x += p.vx * dtFactor
              p.y += p.vy * dtFactor

              if (p.y > floorY) {
                p.y = floorY
                p.vy *= -0.6
                p.vx *= 0.8
              }
            }

            // Fade out splashes earlier than the cloud
            // Let's say splashes last ~2s
            const splashLife = 2000
            if (e.t < splashLife) {
              const splashAlpha = 1 - (e.t / splashLife)
              ctx.globalAlpha = splashAlpha

              const speed = Math.hypot(p.vx, p.vy)
              const angle = Math.atan2(p.vy, p.vx)

              ctx.save()
              ctx.translate(p.x, p.y)
              ctx.rotate(angle)
              ctx.scale(1 + speed * 0.05, 1 - speed * 0.05)

              ctx.beginPath()
              ctx.arc(0, 0, p.size, 0, Math.PI * 2)
              ctx.fillStyle = p.color
              ctx.fill()

              ctx.beginPath()
              ctx.arc(-p.size * 0.3, -p.size * 0.3, p.size * 0.3, 0, Math.PI * 2)
              ctx.fillStyle = 'rgba(255,255,255,0.4)'
              ctx.fill()

              ctx.restore()
            }
          })

          ctx.restore()
        } else if (e.kind === 'steal') {
          if (!renderStealEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'doubleCross') {
          if (!renderDoubleCrossEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'swordOfRevenge') {
          if (!renderSwordOfRevengeEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'ancientSword') {
          if (!renderAncientSwordEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'ancientShield') {
          if (!renderAncientShieldEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'steelShield') {
          if (!renderSteelShieldEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'shimmeringShield') {
          if (!renderShimmeringShieldEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'theSting') {
          if (!renderTheStingEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'cursedIron') {
          if (!renderCursedIronEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'ale') {
          if (!renderAleEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'bodyUpgrade') {
          if (!renderBodyUpgradeEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'arcaneBoots') {
          if (!renderArcaneBootsEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'spikes') {
          if (!renderSpikesEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'heal') {
          if (!renderHealEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'shock') {
          if (!renderShockEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
          // Removed inline shock implementation
        } else if (e.kind === 'energize') {
          if (!renderEnergizeEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'psySearch') {
          const maxTime = 2500
          const tt = e.t / maxTime
          if (tt > 1.2) { effs.splice(i, 1); continue }
          const data = e.data!

          // Physics Update
          data.nodes.forEach((node: any, idx: number) => {
            node.size = node.baseSize + Math.sin(e.t * 0.005 + node.phase) * 3

            data.nodes.forEach((other: any, oIdx: number) => {
              if (idx === oIdx) return
              const dx = node.x - other.x
              const dy = node.y - other.y
              const dist = Math.hypot(dx, dy)
              if (dist < 100) {
                const force = (100 - dist) * 0.05
                node.vx += (dx / dist) * force * dtFactor
                node.vy += (dy / dist) * force * dtFactor
              }
            })

            const cx = canvas.width / 2
            const cy = canvas.height / 2
            const dx = cx - node.x
            const dy = cy - node.y
            node.vx += dx * 0.001 * dtFactor
            node.vy += dy * 0.001 * dtFactor

            node.x += node.vx * dtFactor
            node.y += node.vy * dtFactor
            node.vx *= Math.pow(0.92, dtFactor)
            node.vy *= Math.pow(0.92, dtFactor)
          })

          if (Math.random() < 0.1 * dtFactor) {
            const conn = data.connections[Math.floor(Math.random() * data.connections.length)]
            conn.pulses.push({ t: 0, speed: 0.02 + Math.random() * 0.02 })
          }
          if (Math.random() < 0.1 * dtFactor) {
            const edge = data.edgeConnections[Math.floor(Math.random() * data.edgeConnections.length)]
            edge.pulses.push({ t: 0, speed: 0.02 + Math.random() * 0.02 })
          }

          ctx.save()
          ctx.globalAlpha = Math.min(1, (1.2 - tt) * 2)

          // Draw Edge Connections (Goo Strings)
          data.edgeConnections.forEach((edge: any) => {
            const node = data.nodes[edge.nodeIndex]
            ctx.beginPath()
            ctx.moveTo(edge.x, edge.y)
            // Bezier curve for gooey effect
            const midX = (edge.x + node.x) / 2
            const midY = (edge.y + node.y) / 2
            const offset = Math.sin(e.t * 0.003 + edge.nodeIndex) * 20
            ctx.quadraticCurveTo(midX + offset, midY + offset, node.x, node.y)
            ctx.strokeStyle = 'rgba(10, 10, 15, 0.8)'
            ctx.lineWidth = 3 + Math.sin(e.t * 0.01) * 1
            ctx.stroke()

            // Draw pulses on edges
            edge.pulses = edge.pulses.filter((p: any) => p.t < 1)
            edge.pulses.forEach((p: any) => {
              p.t += p.speed
              const t = p.t
              const invT = 1 - t
              // Simple linear interpolation for pulse position on curve approximation
              const px = (1 - t) * (1 - t) * edge.x + 2 * (1 - t) * t * (midX + offset) + t * t * node.x
              const py = (1 - t) * (1 - t) * edge.y + 2 * (1 - t) * t * (midY + offset) + t * t * node.y

              ctx.beginPath()
              ctx.arc(px, py, 4, 0, Math.PI * 2)
              ctx.fillStyle = 'rgba(56, 189, 248, 0.9)' // Light blue
              ctx.shadowBlur = 10
              ctx.shadowColor = 'rgba(56, 189, 248, 1)'
              ctx.fill()
              ctx.shadowBlur = 0
            })
          })

          // Draw Node Connections
          data.connections.forEach((conn: any) => {
            const n1 = data.nodes[conn.from]
            const n2 = data.nodes[conn.to]
            ctx.beginPath()
            ctx.moveTo(n1.x, n1.y)
            ctx.lineTo(n2.x, n2.y)
            ctx.strokeStyle = 'rgba(20, 20, 30, 0.6)'
            ctx.lineWidth = 2
            ctx.stroke()

            // Pulses
            conn.pulses = conn.pulses.filter((p: any) => p.t < 1)
            conn.pulses.forEach((p: any) => {
              p.t += p.speed
              const px = n1.x + (n2.x - n1.x) * p.t
              const py = n1.y + (n2.y - n1.y) * p.t
              ctx.beginPath()
              ctx.arc(px, py, 3, 0, Math.PI * 2)
              ctx.fillStyle = 'rgba(56, 189, 248, 0.8)'
              ctx.fill()
            })
          })

          // Draw Nodes
          data.nodes.forEach((node: any) => {
            ctx.beginPath()
            ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2)
            ctx.fillStyle = 'rgba(10, 10, 15, 0.9)'
            ctx.fill()

            // Inner glow
            ctx.beginPath()
            ctx.arc(node.x, node.y, node.size * 0.4, 0, Math.PI * 2)
            ctx.fillStyle = 'rgba(56, 189, 248, 0.6)'
            ctx.shadowBlur = 15
            ctx.shadowColor = 'rgba(56, 189, 248, 0.8)'
            ctx.fill()
            ctx.shadowBlur = 0
          })

          ctx.restore()

        } else if (e.kind === 'psyBlock') {
          if (!renderPsyBlockEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'fireball') {
          if (!renderFireballEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'trembling') {
          if (!renderTremblingEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'lifeDrain') {
          if (!renderLifeDrainEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'sprite') {
          if (!renderSpriteEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'hammerSlam') {
          if (!renderHammerSlamEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'voodooDoll') {
          if (!renderVoodooDollEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'discerningEye') {
          if (!renderDiscerningEyeEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'protector') {
          if (!renderProtectorEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'energySword') {
          if (!renderEnergySwordEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'larcendagger') {
          if (!renderLarcendaggerEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'infernoGauntlet') {
          if (!renderInfernoGauntletEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'flying_scythe') {
          if (!renderFlyingScytheEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'plantedBomb') {
          if (!renderPlantedBombEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'invincible') {
          if (!renderInvincibleEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'lifeCharger') {
          if (!renderLifeChargerEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'brokenSecret') {
          const fx = e.data as BrokenSecretEffect;
          fx.update(dtMs / 1000);
          fx.draw(ctx);
          if (fx.isComplete) {
            effs.splice(i, 1);
          }
        } else if (e.kind === 'sealOfSilence') {
          if (!renderSealOfSilenceEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'sealOfSilence') {
          if (!renderSealOfSilenceEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'eyesOfMadness') {
          if (!renderEyesOfMadnessEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'madnessControl') {
          if (!renderMadnessControlEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'twilightShift') {
          if (!renderTwilightShiftEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'chromabow') {
          if (!renderChromabowEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'astridBow') {
          if (!renderAstridBowEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'shadowBurglary') {
          if (!renderShadowBurglaryEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'thunderSpirit') {
          if (!renderThunderSpiritEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'cardCyclone') {
          if (!renderCardCycloneEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'counterblast') {
          if (!renderCounterblastEffect(e as any, ctx)) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'ale') {
          if (!renderAleEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        } else if (e.kind === 'fortunesFavor') {
          if (!renderFortunesFavorEffect(e as any, ctx, (p) => particlesRef.current.push(p))) {
            effs.splice(i, 1)
          }
        }
      }

      if (hasActiveCanvasWork()) {
        rafRef.current = requestAnimationFrame(step)
      }
    }
    startCanvasLoopRef.current = () => {
      if (!props.enabled || rafRef.current !== null || !hasActiveCanvasWork()) {
        return
      }
      rafRef.current = requestAnimationFrame(step)
    }

    startCanvasLoopRef.current()
    window.addEventListener('resize', resize)
    return () => {
      startCanvasLoopRef.current = null
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      clearCanvas()
      window.removeEventListener('resize', resize)
    }
  }, [props.enabled])

  const remove3DEffect = (id: string) => {
    setEffects3D(prev => prev.filter(e => e.id !== id))
  }

  return (
    <>
      {effects3D.length > 0 && createPortal(
        <Suspense fallback={null}>
          <ThreeEffectsOverlay
            effects={effects3D}
            onComplete={remove3DEffect}
          />
        </Suspense>,
        document.body
      )}
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-40" />
    </>
  )
})

export default ParticleLayer
